import { QueryFactory } from 'services/api';
import {
  OperationType,
  ProjectResourceType,
  TableData,
  VariableLockStatusDto,
} from 'services/api/api-client.types.ts';
import { Loading } from 'components/uikit/suspense/Loading.tsx';
import styles from './PreviewVariableChangesModal.module.scss';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CustomModal } from 'components/uikit/modal/CustomModal.tsx';
import { renderToStaticMarkup } from 'react-dom/server';
import { SimpleHtmlTable } from './SimpleHtmlTable.tsx';
import DiffWorker from './diff.worker.ts?worker';
import { useScopedTranslation } from 'application/localization/useScopedTranslation.ts';
import { Button, ButtonColor } from 'components/uikit/buttons/Button.tsx';
import { useQueryErrorAlert } from 'helpers/error-helpers.ts';
import { withStyles } from '@mui/styles';
import { Tooltip } from '@mui/material';
import { getLockedVariableMessage } from '../variableCard/useVariableCardAppMenuItems.tsx';
import { useHasProjectPermission } from 'helpers/projectPermissions/useHasProjectPermission.ts';
import { permission } from 'helpers/projectPermissions/types/projectPermission.ts';
import { DiffTable } from './DiffTable.tsx';
import { useVariablesContext } from '../../variablesContext/VariablesContext.tsx';

type PreviewVariableChangesModalProps = {
  variableId: number;
  revisionId: number;
  isOpen: boolean;
  onClose: () => void;
  postfix?: string;
  lockStatuses: VariableLockStatusDto[];
  onUpdate: () => Promise<void>;
};

export const PreviewVariableChangesModal = (
  props: PreviewVariableChangesModalProps,
) => {
  const i18n = useScopedTranslation('Component.PreviewVariableChangesModal');
  const [isLoading, setIsLoading] = useState(false);

  const { refreshVariables } = useVariablesContext();

  const currentVariableValueQuery = QueryFactory.VariableQuery.useGetQuery(
    props.variableId,
    props.revisionId,
    props.postfix,
    { enabled: props.isOpen, throwOnError: false },
  );
  useQueryErrorAlert(currentVariableValueQuery, null, props.onClose);

  const fetchedVariableValueQuery =
    QueryFactory.VariableQuery.useGetFetchedValueQuery(
      props.variableId,
      props.revisionId,
      props.postfix,
      { enabled: props.isOpen, throwOnError: false },
    );
  useQueryErrorAlert(fetchedVariableValueQuery, null, props.onClose);

  const currentValueTable = useMemo(() => {
    if (!currentVariableValueQuery.data) return null;
    return JSON.parse(
      currentVariableValueQuery.data.actualValue.value,
    ) as TableData;
  }, [currentVariableValueQuery.data]);
  const fetchedValueTable = useMemo(() => {
    if (!fetchedVariableValueQuery.data) return null;
    return JSON.parse(fetchedVariableValueQuery.data) as TableData;
  }, [fetchedVariableValueQuery.data]);

  // Refresh variables is called only if newer variable value is available.
  const updatedVariables = useRef(false);
  useEffect(() => {
    if (
      updatedVariables.current ||
      !currentVariableValueQuery.data ||
      !fetchedVariableValueQuery.data ||
      currentVariableValueQuery.data.actualValue.hasUpdatedData
    )
      return;

    if (
      currentVariableValueQuery.data.actualValue.value !==
      fetchedVariableValueQuery.data
    ) {
      updatedVariables.current = true;
      void refreshVariables();
    }
  }, [currentVariableValueQuery.data, fetchedVariableValueQuery.data]);

  const currentTableHtml = useMemo(() => {
    if (!currentValueTable) return '';

    const jsx = <SimpleHtmlTable table={currentValueTable} />;

    return renderToStaticMarkup(jsx);
  }, [currentValueTable]);

  const fetchedTableHtml = useMemo(() => {
    if (!fetchedValueTable) return '';

    const jsx = <SimpleHtmlTable table={fetchedValueTable} />;

    return renderToStaticMarkup(jsx);
  }, [fetchedValueTable]);

  const [comparedTable, setComparedTable] = useState('');
  const [worker] = useState(() => new DiffWorker());
  useEffect(() => {
    if (!currentTableHtml || !fetchedTableHtml) return;

    const handleMessage = (event: MessageEvent) => {
      setComparedTable(event.data);
    };
    worker.addEventListener('message', handleMessage);
    worker.postMessage({
      currentHtml: currentTableHtml,
      fetchedHtml: fetchedTableHtml,
    });

    return () => {
      worker.removeEventListener('message', handleMessage);
    };
  }, [currentTableHtml, fetchedTableHtml, worker]);

  const isReadOnlyMode = props.lockStatuses.length > 0;
  const hasPermission = useHasProjectPermission();
  const hasUpdateVariablePermission = hasPermission([
    permission(ProjectResourceType.Variable, OperationType.Update),
  ]);
  const canUpdateVariable = !isReadOnlyMode && hasUpdateVariablePermission;

  return (
    <CustomModal
      title={i18n.t('title')}
      isOpen={props.isOpen}
      onClose={props.onClose}
      classes={modalClasses}
      isBlocking
    >
      <Loading
        loading={
          fetchedVariableValueQuery.isLoading || !comparedTable || isLoading
        }
      >
        {comparedTable && <DiffTable html={comparedTable} />}
      </Loading>
      <div className={styles.footer}>
        <Button
          title={i18n.t('buttons.cancel')}
          color={ButtonColor.Secondary}
          className={styles.button}
          onClick={props.onClose}
        />
        <StyledTooltip
          PopperProps={{
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -8],
                },
              },
            ],
          }}
          title={
            <span className={styles.tooltipTitle}>
              {!hasUpdateVariablePermission
                ? i18n.t('cant_update_due_to_permissions')
                : isReadOnlyMode
                  ? getLockedVariableMessage(props.lockStatuses)
                  : null}
            </span>
          }
          disableHoverListener={canUpdateVariable}
        >
          <span>
            <Button
              disabled={!canUpdateVariable}
              title={i18n.t('buttons.update')}
              color={ButtonColor.Default}
              className={styles.button}
              onClick={async (e) => {
                setIsLoading(true);

                e.stopPropagation();
                await props.onUpdate();
                props.onClose();

                setIsLoading(false);
              }}
            />
          </span>
        </StyledTooltip>
      </div>
    </CustomModal>
  );
};

const modalClasses = {
  paper: styles.modal,
  content: styles.modalContent,
};

const StyledTooltip = withStyles({
  tooltip: {
    backgroundColor: '#ffffff !important',
    color: '#000000 !important',
    boxShadow: '0px 0px 15px rgba(0, 0, 0, 0.12)',
    borderRadius: '2px',
  },
})(Tooltip);
